// Frontend helpers voor de centrale cascade-filters energielabel → verkoopteam →
// verkoper. De structuur komt van GET /api/reference/sales-structure en is al
// rol-gescopet door de backend (een teamleader krijgt alleen zijn eigen label/
// team/verkopers). Deze helpers leiden er de dropdown-opties uit af, zodat ALLE
// modules exact dezelfde, consistente cascade tonen — losgekoppeld van de data
// die een specifieke module toevallig bevat.
//
// Structuur-vorm:
//   {
//     energy_labels: string[],
//     teams_by_label: { [label]: string[] },
//     sellers_by_label_team: { [label]: { [team]: string[] } },
//     unmatched: { name, label }[]   // alleen admin
//   }

export const EMPTY_STRUCTURE = {
  energy_labels: [],
  teams_by_label: {},
  sellers_by_label_team: {},
  unmatched: [],
};

const uniqSort = (arr) => [...new Set(arr)].sort((a, b) => a.localeCompare(b));

// Alle teams over alle labels heen (voor company === 'all').
function allTeams(structure) {
  const out = [];
  for (const label of Object.keys(structure.teams_by_label || {})) {
    out.push(...(structure.teams_by_label[label] || []));
  }
  return uniqSort(out);
}

// Alle verkopers die voldoen aan (company, team) waarbij 'all' = geen filter.
function collectSellers(structure, company, team) {
  const slt = structure.sellers_by_label_team || {};
  const labels = company === 'all' ? Object.keys(slt) : [company];
  const out = [];
  for (const label of labels) {
    const byTeam = slt[label] || {};
    const teams = team === 'all' ? Object.keys(byTeam) : [team];
    for (const t of teams) out.push(...(byTeam[t] || []));
  }
  return uniqSort(out);
}

// Geef de drie optie-lijsten voor de huidige selectie. companies is altijd de
// volledige (gescopete) set labels; teams cascadeert op company; sellers
// cascadeert op company + team.
//
// Optioneel `intersect` = { labels?, teams?, sellers? } (arrays). Wanneer
// meegegeven worden de uitkomsten beperkt tot waarden die óók in die lijsten
// voorkomen. Gebruikt door modules met een eigen databron (facturen) waarvan de
// team/verkoper-spelling kan afwijken van /resellers — zo krijg je de uniforme
// centrale cascade zonder dropdown-opties die 0 rijen opleveren.
export function scopeOptions(structure, company = 'all', team = 'all', intersect = null) {
  const s = structure || EMPTY_STRUCTURE;
  let companies = uniqSort(s.energy_labels || []);
  let teams = company === 'all' ? allTeams(s) : uniqSort(s.teams_by_label?.[company] || []);
  let sellers = collectSellers(s, company, team);
  if (intersect) {
    if (intersect.labels) { const set = new Set(intersect.labels); companies = companies.filter(x => set.has(x)); }
    if (intersect.teams) { const set = new Set(intersect.teams); teams = teams.filter(x => set.has(x)); }
    if (intersect.sellers) { const set = new Set(intersect.sellers); sellers = sellers.filter(x => set.has(x)); }
  }
  return { companies, teams, sellers };
}

// Als een eerder gekozen waarde niet meer geldig is binnen de huidige cascade
// (bv. nadat verse structuurdata binnenkwam), val terug op 'all'. Cascade-reset:
// zodra een bovenliggend niveau ongeldig wordt en terugvalt op 'all', resetten
// ook de onderliggende niveaus — net als de handmatige onChange-handlers die bij
// een label-wissel team+verkoper op 'all' zetten. Geeft een gecorrigeerd
// {company, team, seller} terug; ongewijzigd als alles nog geldig is.
export function clampSelection(structure, company = 'all', team = 'all', seller = 'all') {
  const { companies } = scopeOptions(structure, 'all', 'all');
  let c = company;
  let t = team;
  let sel = seller;
  if (c !== 'all' && !companies.includes(c)) { c = 'all'; t = 'all'; sel = 'all'; }
  if (t !== 'all') {
    const { teams } = scopeOptions(structure, c, 'all');
    if (!teams.includes(t)) { t = 'all'; sel = 'all'; }
  }
  if (sel !== 'all') {
    const { sellers } = scopeOptions(structure, c, t);
    if (!sellers.includes(sel)) sel = 'all';
  }
  return { company: c, team: t, seller: sel };
}

// Bepaal de default cascade-selectie op basis van de scope van de ingelogde
// gebruiker. Admin/ET-Dossierbeheerder hebben volledige data-toegang → geen
// vaste scope, dus alles 'all'. Lagere rollen krijgen hun eigen energielabel
// (master), label+team (teamleader) of label+team+verkoper (reseller)
// voorgeselecteerd, zodat de filters meteen op hun toegestane data staan i.p.v.
// "Alle". De velden komen uit /api/auth/me (camelCase). Ontbrekende velden
// vallen terug op 'all'.
export function defaultScopeSelection(user) {
  if (!user || user.role === 'admin' || user.role === 'et_dossier') {
    return { company: 'all', team: 'all', seller: 'all' };
  }
  return {
    company: user.energyLabel || 'all',
    team: user.salesTeam || 'all',
    seller: user.sellerName || 'all',
  };
}

// Scope van een favoriete filtercombinatie: energielabel → verkoopteam → verkoper.
//
// Het ENERGIELABEL HOORT ERBIJ. Het bleef er eerder bewust buiten ("blijft per
// gebruiker"), maar dat is gevaarlijk voor wie meerdere labels ziet — admin en
// ET-dossierbeheerder. Je herstelt dan wel het verkoopteam van label A terwijl de
// pagina nog op label B staat: de tabel toont stil een andere selectie dan de
// favoriet beschrijft, zonder dat er iets fout lijkt.
//
// Rechten oprekken kan het niet. `clampSelection` valideert de hele cascade tegen
// de ROL-GESCOPETE sales-structuur en zet onderliggende niveaus mee terug zodra een
// bovenliggend niveau wegvalt — net als de handmatige onChange-handlers. Een
// gedeelde favoriet met "Servolt Energie" landt bij een Power2You-master dus op
// "Alle", niet op Servolt.
//
// `zichtbareLabels` is de keuzelijst die de pagina zélf toont. Op Invoices is die
// versmald tot labels die in de facturen voorkomen; een label dat daar niet in staat
// zou een lege keuzelijst opleveren, dus dan vallen we terug op "Alle" — inclusief
// team en verkoper, want die horen bij een label dat je niet meer toont.
export function favorietScope(structure, { company, team, seller } = {}, zichtbareLabels = null) {
  const s = clampSelection(structure, company ?? 'all', team ?? 'all', seller ?? 'all');
  if (s.company !== 'all' && Array.isArray(zichtbareLabels) && !zichtbareLabels.includes(s.company)) {
    return { company: 'all', team: 'all', seller: 'all' };
  }
  return s;
}
